import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, BookOpen, PlayCircle } from "lucide-react";
import { apiClient, API } from "@/lib/api";
import { Logo } from "@/components/landing/Logo";
import { Navbar } from "@/components/landing/Navbar";

const youtubeId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : null;
};

const VideoCard = ({ v, i }) => {
  const yt = youtubeId(v.video_url);
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: i * 0.08 }}
      className="luxury-card overflow-hidden rounded-xl"
      data-testid={`video-card-${i}`}
    >
      <div className="aspect-video w-full bg-[#12294D]">
        {yt ? (
          <iframe
            src={`https://www.youtube.com/embed/${yt}`}
            title={v.title}
            className="h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : v.has_file ? (
          <video
            controls
            controlsList="nodownload"
            onContextMenu={(e) => e.preventDefault()}
            preload="metadata"
            className="h-full w-full"
            src={`${API}/video-blogs/${v.id}/file`}
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <PlayCircle size={44} className="text-[#D4AF37]/60" />
          </div>
        )}
      </div>
      <div className="p-6">
        <h3 className="font-serif-display text-xl font-semibold leading-snug text-[#12294D]">{v.title}</h3>
        {v.description && <p className="mt-2 text-sm leading-relaxed text-[#5E6E85]">{v.description}</p>}
      </div>
    </motion.div>
  );
};

export const BlogList = () => {
  const [tab, setTab] = useState("articles");
  const [posts, setPosts] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiClient.get("/blog").catch(() => ({ data: [] })),
      apiClient.get("/video-blogs").catch(() => ({ data: [] })),
    ]).then(([p, v]) => {
      setPosts(p.data || []);
      setVideos(v.data || []);
      setLoading(false);
    });
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF8F2]" data-testid="blog-list-page">
      <Navbar />
      <main className="mx-auto max-w-5xl px-5 pb-16 pt-[132px] sm:px-8 sm:pb-20">
        <p className="mb-4 flex items-center gap-3 font-mono-accent text-[11px] uppercase tracking-[0.35em] text-[#A07E1F]">
          <span className="inline-block h-2 w-2 rotate-45 bg-[#D4AF37]" />
          Study Guides & Insights
        </p>
        <h1 className="font-serif-display text-4xl font-medium tracking-tight text-[#12294D] sm:text-5xl" data-testid="blog-list-heading">
          The Academic <span className="italic text-[#A07E1F]">Library.</span>
        </h1>
        <p className="mt-4 max-w-xl text-[#46586F]">Free, expert-written guides and video blogs on dissertations, referencing, assignment planning and study skills.</p>

        <div className="mt-10 flex gap-2" data-testid="blog-tabs">
          <button
            onClick={() => setTab("articles")}
            data-testid="blog-tab-articles"
            className={`flex items-center gap-2 rounded-full px-6 py-2.5 font-mono-accent text-[11px] uppercase tracking-[0.15em] transition-colors ${tab === "articles" ? "bg-[#D4AF37] text-[#12294D]" : "border border-[#A07E1F]/30 text-[#A07E1F] hover:bg-[#D4AF37]/10"}`}
          >
            <BookOpen size={14} /> Articles ({posts.length})
          </button>
          <button
            onClick={() => setTab("videos")}
            data-testid="blog-tab-videos"
            className={`flex items-center gap-2 rounded-full px-6 py-2.5 font-mono-accent text-[11px] uppercase tracking-[0.15em] transition-colors ${tab === "videos" ? "bg-[#D4AF37] text-[#12294D]" : "border border-[#A07E1F]/30 text-[#A07E1F] hover:bg-[#D4AF37]/10"}`}
          >
            <PlayCircle size={14} /> Video Blogs ({videos.length})
          </button>
        </div>

        {loading ? (
          <p className="mt-16 text-[#5E6E85]" data-testid="blog-loading">Loading...</p>
        ) : tab === "articles" ? (
          posts.length === 0 ? (
            <p className="mt-16 text-[#5E6E85]" data-testid="blog-empty">New guides are being written — check back soon.</p>
          ) : (
            <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
              {posts.map((p, i) => (
                <motion.div key={p.id} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: i * 0.08 }}>
                  <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${i}`} className="luxury-card group flex h-full flex-col rounded-xl p-8">
                    <BookOpen size={20} className="mb-5 text-[#A07E1F]" />
                    <h2 className="font-serif-display text-2xl font-semibold leading-snug text-[#12294D] transition-colors group-hover:text-[#A07E1F]">{p.title}</h2>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-[#46586F]">{p.excerpt}</p>
                    <div className="mt-6 flex items-center justify-between">
                      <div className="flex flex-wrap gap-2">
                        {p.tags?.map((t) => (
                          <span key={t} className="rounded-full bg-[#D4AF37]/15 px-3 py-1 text-[10px] uppercase tracking-[0.15em] text-[#A07E1F]">{t}</span>
                        ))}
                      </div>
                      <ArrowRight size={16} className="shrink-0 text-[#A07E1F] transition-transform duration-300 group-hover:translate-x-1" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )
        ) : videos.length === 0 ? (
          <p className="mt-16 text-[#5E6E85]" data-testid="videos-empty">Video blogs are coming soon — check back shortly.</p>
        ) : (
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2" data-testid="videos-grid">
            {videos.map((v, i) => <VideoCard key={v.id} v={v} i={i} />)}
          </div>
        )}
      </main>
    </div>
  );
};
